import { useState, useEffect, useRef } from 'react'
import { GliderPayload, TakeOffPayload } from './types'
import { Button } from '../components/button'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../components/dialog'
import { Input } from '../components/input'
import { Switch, SwitchField } from '../components/switch'
import { Label } from '../components/fieldset'
import { Combobox, ComboboxOption, ComboboxLabel } from '../components/combobox'
import { partitionGliders } from './glider_visibility'

const FLIGHT_TYPES = ['site flight', 'hike and fly', 'tandem'] as const

/** Build duration options from 10 to 600 minutes in 10-minute steps. */
const DURATION_OPTIONS = Array.from({ length: 60 }, (_, i) => {
  const mins = (i + 1) * 10
  const h = Math.floor(mins / 60)
  const m = mins % 60
  const label = h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`
  return { value: mins, label }
})

export interface ManualFlightData {
  start_time: string
  end_time: string
  type?: string
  glider_id?: string
  takeoff_id?: number
}

interface AddFlightModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (file: File, gliderId?: string) => Promise<void>
  onSubmitManual: (data: ManualFlightData) => Promise<void>
}

interface UploadProgress {
  total: number
  completed: number
  currentName: string
}

export function AddFlightModal({ isOpen, onClose, onSubmit, onSubmitManual }: AddFlightModalProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [gliderId, setGliderId] = useState('')
  const [gliders, setGliders] = useState<GliderPayload[]>([])
  const [allTakeoffs, setAllTakeoffs] = useState<TakeOffPayload[]>([])
  const [selectedTakeoff, setSelectedTakeoff] = useState<TakeOffPayload | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [manualMode, setManualMode] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null)
  const [manualDate, setManualDate] = useState('')
  const [manualTime, setManualTime] = useState('')
  const [manualDuration, setManualDuration] = useState(60)
  const [manualType, setManualType] = useState<string>('site flight')
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      fetch('/v0/gliders').then(r => r.json()).then(d => setGliders(d.gliders ?? []))
      fetch('/v0/takeoffs').then(r => r.json()).then(d => setAllTakeoffs(d.takeoffs ?? []))
    } else {
      setSelectedFiles([])
      setGliderId('')
      setSelectedTakeoff(null)
      setManualMode(false)
      setUploadProgress(null)
      setIsLoading(false)
      setManualDate('')
      setManualTime('')
      setManualDuration(60)
      setManualType('site flight')
    }
  }, [isOpen])

  const resetAndClose = () => {
    setSelectedFiles([])
    setGliderId('')
    setSelectedTakeoff(null)
    setUploadProgress(null)
    setIsLoading(false)
    setManualDate('')
    setManualTime('')
    setManualDuration(60)
    setManualType('site flight')
    onClose()
  }

  const handleModeChange = (v: boolean) => {
    setManualMode(v)
    setSelectedFiles([])
    setSelectedTakeoff(null)
    setManualDate('')
    setManualTime('')
    setManualDuration(60)
    setManualType('site flight')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : []
    setSelectedFiles(files)
  }

  const handleIGCSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (selectedFiles.length === 0) return
    setIsLoading(true)
    const gid = gliderId || undefined

    if (selectedFiles.length === 1) {
      try {
        await onSubmit(selectedFiles[0], gid)
        resetAndClose()
      } catch (error) {
        console.error('Error uploading flight:', error)
        setIsLoading(false)
      }
      return
    }

    setUploadProgress({ total: selectedFiles.length, completed: 0, currentName: selectedFiles[0].name })
    for (let i = 0; i < selectedFiles.length; i++) {
      setUploadProgress({ total: selectedFiles.length, completed: i, currentName: selectedFiles[i].name })
      try {
        await onSubmit(selectedFiles[i], gid)
      } catch (error) {
        console.error(`Error uploading ${selectedFiles[i].name}:`, error)
      }
    }
    setUploadProgress(null)
    setIsLoading(false)
    resetAndClose()
  }

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      const start = new Date(`${manualDate}T${manualTime}`)
      const end = new Date(start.getTime() + manualDuration * 60_000)
      await onSubmitManual({
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        type: manualType || undefined,
        glider_id: gliderId || undefined,
        takeoff_id: selectedTakeoff?.id,
      })
      resetAndClose()
    } catch (error) {
      console.error('Error adding manual flight:', error)
      setIsLoading(false)
    }
  }

  const isUploading = uploadProgress !== null
  const manualReady = manualDate !== '' && manualTime !== ''
  const activeGliders = partitionGliders(gliders).active

  return (
    <Dialog open={isOpen} onClose={isUploading ? () => {} : onClose}>
      <DialogTitle>Add New Flight</DialogTitle>
      <DialogDescription>
        {manualMode
          ? 'Log a flight without an IGC track.'
          : 'Upload one or more IGC files to add flights to your logbook.'}
      </DialogDescription>
      <DialogBody>
        {isUploading ? (
          <div className="space-y-3 py-2">
            <p className="text-sm font-medium text-[#2D3142] dark:text-white">
              Uploading {uploadProgress.completed + 1} of {uploadProgress.total}
            </p>
            <p className="text-xs text-[#4F5D75] dark:text-[#BFC0C0] truncate">
              {uploadProgress.currentName}
            </p>
            <div className="h-2 w-full rounded-full bg-[#BFC0C0]/20 dark:bg-[#4F5D75]/20 overflow-hidden">
              <div
                className="h-full rounded-full bg-[#EF8354] transition-all duration-300"
                style={{ width: `${(uploadProgress.completed / uploadProgress.total) * 100}%` }}
              />
            </div>
            <p className="text-xs text-right text-[#4F5D75] dark:text-[#BFC0C0]">
              {uploadProgress.completed} / {uploadProgress.total} completed
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <SwitchField>
              <Label className="text-sm text-[#4F5D75] dark:text-[#BFC0C0]">Manual entry</Label>
              <Switch color="orange" checked={manualMode} onChange={handleModeChange} />
            </SwitchField>

            {manualMode ? (
              <form onSubmit={handleManualSubmit} id="add-flight-form" className="space-y-3">
                <div className="space-y-1">
                  <label className="text-xs text-[#4F5D75] dark:text-[#BFC0C0]">Date</label>
                  <Input
                    type="date"
                    value={manualDate}
                    onChange={e => setManualDate(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-[#4F5D75] dark:text-[#BFC0C0]">Take-off time</label>
                  <Input
                    type="time"
                    value={manualTime}
                    onChange={e => setManualTime(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-[#4F5D75] dark:text-[#BFC0C0]">Duration</label>
                  <select
                    value={manualDuration}
                    onChange={e => setManualDuration(Number(e.target.value))}
                    className="w-full rounded-md border border-[#BFC0C0]/50 dark:border-[#4F5D75]/50 bg-transparent px-3 py-2 text-sm text-[#2D3142] dark:text-white outline-none focus:border-[#EF8354] transition-colors"
                  >
                    {DURATION_OPTIONS.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
                <select
                  value={manualType}
                  onChange={e => setManualType(e.target.value)}
                  className="w-full rounded-md border border-[#BFC0C0]/50 dark:border-[#4F5D75]/50 bg-transparent px-3 py-2 text-sm text-[#2D3142] dark:text-white outline-none focus:border-[#EF8354] transition-colors"
                >
                  {FLIGHT_TYPES.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                {allTakeoffs.length > 0 && (
                  <Combobox
                    options={allTakeoffs}
                    value={selectedTakeoff ?? undefined}
                    onChange={t => setSelectedTakeoff(t ?? null)}
                    displayValue={t => t?.name}
                    placeholder="Search takeoffs…"
                  >
                    {t => (
                      <ComboboxOption value={t}>
                        <ComboboxLabel>{t.name}</ComboboxLabel>
                      </ComboboxOption>
                    )}
                  </Combobox>
                )}
                {activeGliders.length > 0 && (
                  <select
                    value={gliderId}
                    onChange={e => setGliderId(e.target.value)}
                    className="w-full rounded-md border border-[#BFC0C0]/50 dark:border-[#4F5D75]/50 bg-transparent px-3 py-2 text-sm text-[#2D3142] dark:text-white outline-none focus:border-[#EF8354] transition-colors"
                  >
                    <option value="">— Select glider (optional) —</option>
                    {activeGliders.map(g => (
                      <option key={g.id} value={g.id}>{g.name}{g.brand ? ` (${g.brand})` : ''}</option>
                    ))}
                  </select>
                )}
              </form>
            ) : (
              <form onSubmit={handleIGCSubmit} id="add-flight-form" className="space-y-3">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".igc"
                  multiple
                  onChange={handleFileChange}
                  required
                />
                {selectedFiles.length > 1 && (
                  <p className="text-xs text-[#4F5D75] dark:text-[#BFC0C0]">
                    {selectedFiles.length} files selected
                  </p>
                )}
                {activeGliders.length > 0 && (
                  <select
                    value={gliderId}
                    onChange={e => setGliderId(e.target.value)}
                    className="w-full rounded-md border border-[#BFC0C0]/50 dark:border-[#4F5D75]/50 bg-transparent px-3 py-2 text-sm text-[#2D3142] dark:text-white outline-none focus:border-[#EF8354] transition-colors"
                  >
                    <option value="">— Select glider (optional) —</option>
                    {activeGliders.map(g => (
                      <option key={g.id} value={g.id}>{g.name}{g.brand ? ` (${g.brand})` : ''}</option>
                    ))}
                  </select>
                )}
              </form>
            )}
          </div>
        )}
      </DialogBody>
      {!isUploading && (
        <DialogActions>
          <Button plain onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-flight-form"
            color="airtime"
            disabled={manualMode ? (!manualReady || isLoading) : (selectedFiles.length === 0 || isLoading)}
          >
            {isLoading
              ? (manualMode ? 'Saving...' : 'Uploading...')
              : manualMode
                ? 'Add Flight'
                : selectedFiles.length > 1
                  ? `Add ${selectedFiles.length} Flights`
                  : 'Add Flight'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}
